import React, { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { DatePicker } from 'components/ui/date-picker';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Slider } from 'components/ui/slider';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from 'components/ui/sheet';
import { cn } from 'lib/utils';
import {
  CUSTOM_TIMEFRAME,
  DEFAULT_SHARED,
  EXAMS,
  MAX_GOALS,
  MAX_TARGET_PERCENT,
  MINUTE_OPTIONS,
  MIN_TARGET_PERCENT,
  PRESET_TIMEFRAMES,
  draftsFromPlan,
  earliestExamDay,
  exactSubjectCount,
  goalComplete,
  goalRequest,
  levelOptions,
  subjectOptions,
  toggleExam,
  toggleSubject,
  useSaveGoal,
  type GoalDraft,
  type GoalPlan,
} from 'modules/studyPlan';
import { examKey, levelKey, subjectKey } from 'modules/studyPlan/labels';
import { portalDialogCloseClass } from 'components/portal/upsell';

const labelClass = 'text-sm font-medium text-foreground/90';

function FieldLabel({ id, label, hint }: { id: string; label: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <Label id={id} className={labelClass}>{label}</Label>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

/** A radio with its label, as the test settings pages lay them out. */
function RadioOption({ id, value, children }: { id: string; value: string; children: React.ReactNode }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-3">
      <RadioGroupItem id={id} value={value} />
      <span className="text-sm">{children}</span>
    </label>
  );
}

/** A checkbox with its label, dimmed when it cannot be ticked. */
function CheckOption({ id, checked, disabled, onChange, children }: { id: string; checked: boolean; disabled?: boolean; onChange: () => void; children: React.ReactNode }) {
  return (
    <label htmlFor={id} className={cn('flex items-center gap-3', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}>
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
      <span className="text-sm">{children}</span>
    </label>
  );
}

/** One exam's answers: its grade, level or category, its subjects or languages, and when it is. */
function GoalFields({ draft, onChange }: { draft: GoalDraft; onChange: (draft: GoalDraft) => void }) {
  const { t } = useTranslation();
  const { exam } = draft;
  const levels = levelOptions(exam);
  const subjects = subjectOptions(exam);
  const exact = exactSubjectCount(exam);
  const custom = draft.timeframe === CUSTOM_TIMEFRAME;
  const id = (part: string) => `goal-${exam}-${part}`;

  return (
    <div className="flex flex-col gap-5">
      {levels.length > 0 && (
        <div className="flex flex-col gap-2">
          <FieldLabel id={id('level-label')} label={t(`studyPlan.field_level_${exam}`)} />
          <Select value={draft.level} onValueChange={(level) => onChange({ ...draft, level })}>
            <SelectTrigger id={id('level')} aria-labelledby={id('level-label')}>
              <SelectValue placeholder={t('studyPlan.field_choose')} />
            </SelectTrigger>
            <SelectContent>
              {levels.map((level) => (
                <SelectItem key={level} value={level}>{t(levelKey(level))}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {subjects.length > 0 && (
        <div role="group" aria-labelledby={id('subjects-label')} className="flex flex-col gap-2">
          <FieldLabel
            id={id('subjects-label')}
            label={t(exam === 'LANGUAGES' ? 'studyPlan.field_languages' : 'studyPlan.field_subjects')}
            hint={exact !== null ? t('studyPlan.field_subjects_hint', { count: exact }) : t('studyPlan.field_subjects_any')}
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {subjects.map((subject) => {
              const selected = draft.subjects.includes(subject);
              return (
                <CheckOption
                  key={subject}
                  id={id(`subject-${subject}`)}
                  checked={selected}
                  disabled={!selected && exact !== null && draft.subjects.length >= exact}
                  onChange={() => onChange(toggleSubject(draft, subject))}
                >
                  {t(subjectKey(subject))}
                </CheckOption>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <FieldLabel id={id('date-label')} label={t('studyPlan.field_date')} />
        <RadioGroup aria-labelledby={id('date-label')} value={draft.timeframe} onValueChange={(timeframe) => onChange({ ...draft, timeframe })} className="grid-cols-2 gap-x-4 gap-y-3">
          {[...PRESET_TIMEFRAMES, CUSTOM_TIMEFRAME].map((option) => (
            <RadioOption key={option} id={id(`timeframe-${option}`)} value={option}>{t(`studyPlan.timeframe_${option}`)}</RadioOption>
          ))}
        </RadioGroup>
        {custom && (
          <DatePicker
            id={id('date')}
            ariaLabel={`${t(examKey(exam))}: ${t('studyPlan.field_date')}`}
            min={earliestExamDay()}
            value={draft.examDate}
            onChange={(examDate) => onChange({ ...draft, examDate })}
          />
        )}
      </div>
    </div>
  );
}

interface GoalSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The saved goals; empty to set a first one. */
  goals: GoalPlan[];
  /** What the goals share, or null for a learner with none yet. */
  shared: { dailyStudyMinutes: number; targetPercent: number } | null;
  /** Start at this exam's answers: one with a question missing. */
  focusExam?: string;
}

/**
 * The goal form: the exams the learner prepares for (up to MAX_GOALS), each with its own answers and date, and
 * the time a day and target they share. The parent remounts it (by key) on each open so it starts from the saved goals.
 */
export function GoalSheet({ open, onOpenChange, goals, shared, focusExam }: GoalSheetProps) {
  const { t } = useTranslation();
  const save = useSaveGoal();
  const [drafts, setDrafts] = useState<GoalDraft[]>(() => draftsFromPlan(goals));
  const [dailyStudyMinutes, setMinutes] = useState(() =>
    shared && (MINUTE_OPTIONS as readonly number[]).includes(shared.dailyStudyMinutes) ? shared.dailyStudyMinutes : DEFAULT_SHARED.dailyStudyMinutes,
  );
  const [targetPercent, setTarget] = useState(shared?.targetPercent ?? DEFAULT_SHARED.targetPercent);
  const [error, setError] = useState<string | null>(null);
  const focusRef = useRef<HTMLFieldSetElement>(null);

  const canSave = !save.isPending && drafts.length > 0 && drafts.every((draft) => goalComplete(draft));
  const update = (next: GoalDraft) => setDrafts((current) => current.map((draft) => (draft.exam === next.exam ? next : draft)));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setError(null);
    save.mutate(
      { goals: drafts.map(goalRequest), dailyStudyMinutes, targetPercent },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err: unknown) =>
          setError(isAxiosError(err) && err.response?.data?.code === 'EXAM_DATE_OUT_OF_RANGE' ? t('studyPlan.date_error') : t('studyPlan.save_error')),
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        closeLabel={t('studyPlan.cancel')}
        closeClassName={portalDialogCloseClass}
        className="w-full overflow-y-auto pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-md"
        onOpenAutoFocus={(event) => {
          if (focusExam && focusRef.current) {
            event.preventDefault();
            focusRef.current.focus();
          }
        }}
      >
        <SheetHeader className="pr-6">
          <SheetTitle>{goals.length > 0 ? t('studyPlan.sheet_title_edit') : t('studyPlan.sheet_title_new')}</SheetTitle>
          <SheetDescription>{t('studyPlan.sheet_intro')}</SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-6" noValidate>
          <div role="group" aria-labelledby="goal-exams-label" className="flex flex-col gap-2">
            <FieldLabel id="goal-exams-label" label={t('studyPlan.field_exams')} hint={t('studyPlan.field_exams_hint', { count: MAX_GOALS })} />
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {EXAMS.map((exam) => {
                const selected = drafts.some((draft) => draft.exam === exam);
                return (
                  <CheckOption
                    key={exam}
                    id={`goal-exam-${exam}`}
                    checked={selected}
                    disabled={!selected && drafts.length >= MAX_GOALS}
                    onChange={() => setDrafts((current) => toggleExam(current, exam))}
                  >
                    {t(examKey(exam))}
                  </CheckOption>
                );
              })}
            </div>
          </div>

          {drafts.map((draft) => (
            <fieldset
              key={draft.exam}
              ref={draft.exam === focusExam ? focusRef : undefined}
              tabIndex={draft.exam === focusExam ? -1 : undefined}
              className="rounded-md border border-border p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <legend className="px-1 text-[15px] font-semibold text-foreground">{t(examKey(draft.exam))}</legend>
              <GoalFields draft={draft} onChange={update} />
            </fieldset>
          ))}

          <div className="flex flex-col gap-2">
            <FieldLabel id="goal-minutes-label" label={t('studyPlan.field_minutes')} hint={drafts.length > 1 ? t('studyPlan.field_minutes_shared') : undefined} />
            <RadioGroup
              aria-labelledby="goal-minutes-label"
              value={String(dailyStudyMinutes)}
              onValueChange={(minutes) => setMinutes(Number(minutes))}
              className="grid-cols-2 gap-x-4 gap-y-3"
            >
              {MINUTE_OPTIONS.map((minutes) => (
                <RadioOption key={minutes} id={`goal-minutes-${minutes}`} value={String(minutes)}>{t(`studyPlan.minutes_${minutes}`)}</RadioOption>
              ))}
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="goal-target" className={labelClass}>{t('studyPlan.field_target')}</Label>
              <span className="text-xs text-muted-foreground">{`${targetPercent}%`}</span>
            </div>
            <Slider
              id="goal-target"
              ariaLabel={t('studyPlan.field_target')}
              min={MIN_TARGET_PERCENT}
              max={MAX_TARGET_PERCENT}
              step={5}
              value={targetPercent}
              onChange={setTarget}
            />
          </div>

          {error && <p role="alert" className="rounded-md bg-error-50 px-3 py-2 text-sm text-error-700 dark:bg-error-900/30 dark:text-error-400">{error}</p>}

          <SheetFooter className="gap-2 sm:space-x-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('studyPlan.cancel')}</Button>
            <Button type="submit" disabled={!canSave}>{save.isPending ? t('studyPlan.saving') : t('studyPlan.save')}</Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default GoalSheet;
